import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';

import store from '../../../store';
import {conditional, conditionalWithConditions} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectSpcAttributes,
	selectProductData,
	selectSystemLumen,
	selectDimmLevel,
	selectCoefficients,
	selectHasCoefficients, selectSpcConfigurations
} from '../../../selectors';
import {regionCheck} from '../../../util/regionCheck';
import {calculateCurrent, calculatePower} from '../../../util/dimLevel';
import {attributeIdToString, stringToAttributeId} from '../../../components/spc/util/spcUtils';
import {olivioHeadTypeOptions} from '../../olivioModel';

@customElement('olivio-specsheet-head-data')
export class OlivioSpecsheetHeadData extends connect(store)(LitElement) {

	@property({type: Object})
	headData = null;

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}
			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	constructor() {
		super();
		this.productData = null;
		this.satisfiedAttributes = [];
		this.unSatisfiedAttributes = [];
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.productData = selectProductData(this.state);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.instanceId = this.headData.instanceId;

		// excluded attributes, that are defined individually in heads
		const excludeAttributes = [
			'LE_ReflectorColour_1',
			'LE_LightDistribution_1',
			'LE_ColourTemperature_1',
			'HS_HousingColour',
			'HS_HousingSize',
			'CX_Region',
			'HS_CircularBaseProfile',
			'LE_LuminousFlux_1',
			//'LE_DimmingLevel_1',
		];

		this.spcConfigurations = selectSpcConfigurations(state);
		if (this.spcConfigurations && Object.hasOwn(this.spcConfigurations, this.instanceId)) {
			this.spcConfiguration = this.spcConfigurations[this.instanceId];
			this.spcAttributes = Array.from(this.spcConfiguration.attributes.values());
		}
		if (this.spcAttributes?.length > 0 && this.productData) {
			this.spcAttributes = this.spcAttributes
				.filter(spcAttribute => {
					const featureProductData = getProductData(spcAttribute.id.localId, this.productData);
					return this.debugShowAll || featureProductData?.isVisible && regionCheck(featureProductData, this.region);
				}) // filter when configured to not be visible in PIM
				.filter(spcAttribute => !excludeAttributes.includes(spcAttribute.id.localId))
				.sort((a, b) =>
					getProductData(a.id.localId, this.productData).displaySequenceUi -
					getProductData(b.id.localId, this.productData).displaySequenceUi
				);
			this.satisfiedAttributes = this.spcAttributes.filter(attribute => attribute.isSatisfied);
			this.unSatisfiedAttributes = this.spcAttributes.filter(attribute => !attribute.isSatisfied);
		}

	}
	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const dimensionsLabel = this.t('TE_Dimensions', 'D');

		this.housingSize = this.headData.head.housingSize;

		const conditions = [
			'HS_HousingSize.' + this.headData.head.housingSize,
			'LE_ColourTemperature_1.' + this.headData.head.colourTemperature,
			'LE_LightDistribution_1.' + this.headData.head.lightDistribution,
			'LE_DimmingLevel_1.' + getValueOfFeature('root::LE_DimmingLevel_1', this.spcConfiguration),
			'LE_ReflectorColour_1.' + getValueOfFeature('root::LE_ReflectorColour_1', this.spcConfiguration),
			'LE_Accessories_1.' + getValueOfFeature('root::LE_Accessories_1', this.spcConfiguration),
		];
		const dimensionWidth = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Width', 'EditorialTable', 1);
		const dimensionHeight = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Height', 'EditorialTable', 1);
		const dimensionDepth = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Depth', 'EditorialTable', 1);
		const dimensionDiameter = conditionalWithConditions(conditions, this.conditionalVariantValues, 'Dimension_Diameter', 'EditorialTable', 1);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight || dimensionDiameter;
		const weight = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_LU_Weight', 'EditorialTable', 1);

		const wattageMax = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max', 'EditorialTable', 1);
		const accessory = getValueOfFeature('root::LE_Accessories_1', this.spcConfiguration);

		const accessoryFactorValue = conditionalWithConditions(
			[
				'LE_Accessory.' + accessory,
				'LE_LightDistribution_1.' + this.headData.head.lightDistribution,
				'HS_HousingSize.' + this.headData.head.housingSize
			],
			this.conditionalVariantValues, 'REDTAB.RETURN_LE_AcessoryFactor', 'EditorialTable'
		);
		const accessoryFactor = accessoryFactorValue ? parseFloat(accessoryFactorValue.replace(',', '.')) : 1;
		// console.log('accessoryFactor', accessoryFactor, accessoryFactorValue);

		const applicableStandards = [
			{key: 'LU_IP.IP66', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'LU_IK.10', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'EB_ElectricalProtectionClass_1', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LU_ENEC_CertificateDescription.ENEC', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'LU_CE.CE', labelContext: 'DV.S', valueContext: 'DV.S'}
		];
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;

		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');

		const maxLumenValue = getValueOfFeature('LE_DimmingLevel_1', this.spcConfiguration);
		const maxLumenPerWatt = Math.round(maxLumenValue / parseInt(wattageMax, 10) * 100) / 100;

		const type = this.headData.head.type;
		const olivioHeadTypeOption = olivioHeadTypeOptions.find((option) => option.id === type);
		const typeLabel = olivioHeadTypeOption.label[this.lang];
		const housingSize = this.headData.head.housingSize;

		// get the sx number from CCV
		let sx = '';
		const conditionalVariantValues = this.state.productData.conditionalVariantValues;
		if (conditionalVariantValues) {
			sx = conditional(this.spcConfiguration, conditionalVariantValues, 'LU_LegacyArticleNumberGerman');
		}
		return html`

			<h3 class="subheadline">${msg('Selected Properties')}</h3>

			${when(this.headData.head.type === 'gobo' || this.headData.head.type === 'camera', () => html`
				<shared-specsheet-feature>
					<span slot="label">SX</span>
					<span slot="value">
						${olivioHeadTypeOption.number}
					</span>
				</shared-specsheet-feature>
			`)}
			${when(this.headData.head.type === 'speaker', () => html`
				<shared-specsheet-feature>
					<span slot="label">SX</span>
					<span slot="value">
						${olivioHeadTypeOption.number[housingSize]}
					</span>
				</shared-specsheet-feature>
			`)}

			${when(this.headData.head.type === 'gobo', () => html`
				<shared-specsheet-feature>
					<span slot="label">${msg('Focal Length')}</span>
					<span slot="value">${this.headData.head.goboAperture}</span>
				</shared-specsheet-feature>
			`)}

			${when(this.headData.head.type === 'luminaire', () => html`
				<shared-specsheet-feature>
					<span slot="label">SX</span>
					<span slot="value">
						${sx}
					</span>
				</shared-specsheet-feature>
				<shared-specsheet-feature>
					<trans-label slot="label" key="HS_HousingSize" context="F"></trans-label>
					<trans-label slot="value" instanceId="${this.instanceId}" key="HS_HousingSize" value="${this.headData.head.housingSize}" context="FV.S"></trans-label>,
				</shared-specsheet-feature>
				<shared-specsheet-feature>
					<trans-label slot="label" key="LE_LightDistribution_1" context="F"></trans-label>
					<trans-label slot="value" instanceId="${this.instanceId}" key="LE_LightDistribution_1" value="${this.headData.head.lightDistribution}" context="FV.S"></trans-label>,
				</shared-specsheet-feature>
				<shared-specsheet-feature>
					<trans-label slot="label" key="LE_ColourTemperature_1" context="F"></trans-label>
					<trans-label slot="value" instanceId="${this.instanceId}" key="LE_ColourTemperature_1" value="${this.headData.head.colourTemperature}" context="FV.S"></trans-label>,
				</shared-specsheet-feature>


				${when(this.satisfiedAttributes.length > 0, () => html`
					${repeat(this.satisfiedAttributes,
						(attribute) => attributeIdToString(attribute.id),
						(attribute) => html`
							<shared-specsheet-feature condition="${this.t(attribute.id.localId, 'FV.S')}">
								<trans-label slot="label" key="${attribute.id.localId}" context="F"></trans-label>
								<spc-values-label instanceId="${this.instanceId}" slot="value" class="value" attributeKey="${attribute.id.localId}" context="FV.S"
												  .showIdWhenMissing=${true}></spc-values-label>
							</shared-specsheet-feature>
						`)}
				`)}
				<h3 class="subheadline">${msg('Light Output')} / ${msg('System Power')}</h3>

				<shared-specsheet-feature>
					<span slot="label">${msg('max')}. <trans-label slot="label" key="LE_DimmingLevel_1" context="F"></trans-label></span>
					<span slot="value">
					${maxLumenValue} lm / ${maxLumenPerWatt} lm/W<br>
					${when(accessoryFactor !== 1, () => html`
						(${msg('incl.')} <trans-label slot="value" instanceId="${this.instanceId}" key="LE_Accessories_1" context="FV.S" showIdWhenMissing=${true}></trans-label>: ${Math.round(maxLumenValue * accessoryFactor)} lm / ${Math.round(maxLumenPerWatt * accessoryFactor * 100)/100} lm/W)
					`)}
				</span>
				</shared-specsheet-feature>
				<shared-specsheet-feature>
					<span slot="label">${msg('max')}. <trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label>
						</trans-label></span>
					<span slot="value">${wattageMax} W</span>
				</shared-specsheet-feature>

				<h3 class="subheadline">${msg('Attributes')}</h3>

				<!-- Modell -->
				${when(this.t('HS_HousingType', 'FV.S') !== '', () => html`
					<shared-specsheet-feature>
						<trans-label slot="label" key="HS_HousingType" context="F"></trans-label>
						<spc-values-label slot="value" instanceId="${this.instanceId}" attributeKey="HS_HousingType" context="FV.S"></spc-values-label>
					</shared-specsheet-feature>
				`)}


				<shared-specsheet-feature condition="${isDimensionsPresent}">
					<trans-label slot="label" key="TE_Dimensions" context="D"></trans-label>
					<span slot="value" class="value">
					        ${dimensionDiameter ? html`${dimensionDiameter}
							<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionDepth ? html`${dimensionDepth}
							<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionWidth ? html`${dimensionWidth}
							<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
					        ${dimensionHeight ? html`${dimensionHeight}
							<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>` : ''}
					    </span>
				</shared-specsheet-feature>

				<!-- Gewicht -->
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_LuminaireWeight" context="D"></trans-label>
					<span slot="value">${weight + this.t('LU_LuminaireWeightUnit', 'D', null, ' ')}</span>
				</shared-specsheet-feature>

			`)}
        `;
	}
}
